import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api, imageUrl, formatPrice, formatKm } from "../lib/api";
import { Mail, Phone, Trash2, Calendar, User, Car as CarIcon, Banknote, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const STATUS_LABELS = {
  new: "Nuova",
  evaluating: "In valutazione",
  contacted: "Contattato",
  purchased: "Acquistata",
  rejected: "Rifiutata",
};
const STATUS_STYLES = {
  new: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  evaluating: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  contacted: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  purchased: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30",
};

export default function AdminSellOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null); // storage_path
  const [regeneratingId, setRegeneratingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sell-offers");
      setOffers(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const regenerateEstimate = async (id) => {
    setRegeneratingId(id);
    try {
      const r = await api.post(`/sell-offers/${id}/regenerate-estimate`);
      setOffers((prev) => prev.map((o) => (o.id === id ? { ...o, internal_estimate: r.data.internal_estimate } : o)));
      toast.success("Stima AI rigenerata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rigenerazione");
    } finally {
      setRegeneratingId(null);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/sell-offers/${id}`, { status });
      toast.success("Stato aggiornato");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sell-offers/${id}`);
      toast.success("Proposta eliminata");
      fetchAll();
    } catch { toast.error("Errore"); }
  };

  const filtered = filter === "all" ? offers : offers.filter((o) => o.status === filter);

  return (
    <AdminShell title="Proposte Acquisto">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {["all", "new", "evaluating", "contacted", "purchased", "rejected"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 text-xs uppercase tracking-widest rounded-sm transition-colors ${
                filter === s ? "bg-gold text-midnight font-semibold" : "bg-midnight-light text-white/60 hover:text-white"
              }`}
              data-testid={`sell-filter-${s}`}
            >
              {s === "all" ? "Tutte" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
        <p className="text-xs text-white/40 md:ml-auto">{filtered.length} proposte</p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50">Caricamento...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-midnight-light border border-white/5 p-16 text-center">
          <Banknote size={36} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/60">Nessuna proposta in questa categoria</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="sell-offers-list">
          {filtered.map((o) => (
            <div key={o.id} className="bg-midnight-light border border-white/5 p-6" data-testid={`sell-offer-${o.id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold/20 flex items-center justify-center shrink-0">
                    <User size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{o.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-white/60">
                      <a href={`tel:${o.phone}`} className="flex items-center gap-1 hover:text-gold" data-testid={`sell-phone-${o.id}`}>
                        <Phone size={11} /> {o.phone}
                      </a>
                      <a href={`mailto:${o.email}`} className="flex items-center gap-1 hover:text-gold" data-testid={`sell-email-${o.id}`}>
                        <Mail size={11} /> {o.email}
                      </a>
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> {new Date(o.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className={`w-48 rounded-sm border ${STATUS_STYLES[o.status]}`} data-testid={`sell-status-${o.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-destructive hover:bg-destructive hover:text-white rounded-sm transition-colors" data-testid={`delete-sell-${o.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questa proposta?</AlertDialogTitle>
                        <AlertDialogDescription>L'operazione è irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(o.id)} className="bg-destructive text-white">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-white/5 text-xs">
                <div className="col-span-2">
                  <p className="text-white/40 uppercase tracking-widest mb-1">Auto</p>
                  <p className="font-medium flex items-center gap-2"><CarIcon size={12} className="text-gold" />{o.brand} {o.model}</p>
                  {(o.fuel || o.transmission || o.condition) && (
                    <p className="text-[10px] text-white/50 mt-1">
                      {[o.fuel, o.transmission, {excellent:"Ottimo",good:"Buono",fair:"Da sistemare"}[o.condition]].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Anno</p>
                  <p className="font-medium">{o.year}</p>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Km</p>
                  <p className="font-medium">{formatKm(o.km)}</p>
                </div>
                <div>
                  <p className="text-white/40 uppercase tracking-widest mb-1">Prezzo richiesto</p>
                  <p className="font-medium text-gold">{formatPrice(o.requested_price)}</p>
                </div>
              </div>

              {o.internal_estimate && o.internal_estimate.min && (
                <div className="mt-4 pt-4 border-t border-gold/20 bg-gold/5 p-4 -mx-6 -mb-2 md:mx-0 md:mb-0" data-testid={`sell-estimate-${o.id}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-gold text-lg">🔒</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">Stima AI · solo per te</p>
                        <span className="text-[9px] px-2 py-0.5 border border-gold/40 text-gold rounded-sm uppercase">
                          conf. {o.internal_estimate.confidence}
                        </span>
                        <button
                          onClick={() => regenerateEstimate(o.id)}
                          disabled={regeneratingId === o.id}
                          className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 border border-gold/40 text-gold hover:bg-gold hover:text-midnight rounded-sm transition-colors disabled:opacity-50"
                          data-testid={`regenerate-estimate-${o.id}`}
                          title="Rilancia la valutazione AI con i dati attuali"
                        >
                          <RefreshCw size={11} className={regeneratingId === o.id ? "animate-spin" : ""} />
                          {regeneratingId === o.id ? "Attendi..." : "Rigenera"}
                        </button>
                      </div>
                      <p className="text-lg font-display font-semibold text-white">
                        {formatPrice(o.internal_estimate.min)} <span className="text-white/40">–</span> {formatPrice(o.internal_estimate.max)}
                      </p>
                      {o.internal_estimate.notes && (
                        <p className="text-[11px] text-white/70 mt-2 leading-relaxed italic">{o.internal_estimate.notes}</p>
                      )}
                      <p className="text-[11px] text-white/60 mt-2">Il cliente NON vede questa stima. Usala come guida, poi decidi tu il prezzo finale.</p>
                    </div>
                  </div>
                </div>
              )}
              {!o.internal_estimate && (
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
                  <p className="text-xs text-white/50">Nessuna stima AI disponibile per questa proposta.</p>
                  <button
                    onClick={() => regenerateEstimate(o.id)}
                    disabled={regeneratingId === o.id}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold hover:text-midnight rounded-sm transition-colors disabled:opacity-50"
                    data-testid={`generate-estimate-${o.id}`}
                  >
                    <RefreshCw size={11} className={regeneratingId === o.id ? "animate-spin" : ""} />
                    {regeneratingId === o.id ? "Genero..." : "Genera stima AI"}
                  </button>
                </div>
              )}

              {o.images && o.images.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-white/40 uppercase tracking-widest text-xs mb-2">Foto ({o.images.length})</p>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {o.images.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setLightbox(img.storage_path)}
                        className="aspect-square overflow-hidden border border-white/10 hover:border-gold transition-colors"
                        data-testid={`sell-photo-${img.id}`}
                      >
                        <img src={imageUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {o.notes && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-white/40 uppercase tracking-widest text-xs mb-2">Note del cliente</p>
                  <p className="text-sm text-white/80 whitespace-pre-line leading-relaxed">{o.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="sell-photo-lightbox"
        >
          <img src={imageUrl(lightbox)} className="max-w-full max-h-[90vh] object-contain" alt="" />
        </div>
      )}
    </AdminShell>
  );
}
